/* eslint-disable complexity */
import { dataLayerPush as baseDataLayerPush } from '@r1-frontend/analytics/dataLayerPush';
import { ACTIONS, CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { EPriceTypes } from '@r1-frontend/shared/types/EPriceTypes';
import { BaseButton, btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import Link from 'next/link';
import React, { useCallback, useMemo, useState } from 'react';

import { fullBuyEquipAnalyticsBase } from '~/src/analytics';
import dataLayerPush from '~/src/components/ProductCard/analytics/dataLayerPush';
import { EQUIPMENT_ROUTES_MAP } from '~/src/components/ProductCard/constants';
import getShopCategoryByRoute from '~/src/components/ProductCard/helpers/getShopCategoryByRoute';
import * as ST from '~/src/components/ProductCard/styled';
import { isCreditAbnEnabled } from '~/src/features/work-in-home-ab/components/Description/components/Credit/constants/abnVariants';
import { useRouterChange } from '~/src/helpers/hooks/useRouterChange';
import { toCurrency } from '~/src/helpers/numbers';

const PurchaseBlock = (
    {
        dataTest,
        name,
        orderClickHandler,
        loading,
        priceOptions,
        orderText,
        selectedOption,
        setSelectedOption,
        withSelect = true,
        isSelectDisable = false,
        isBtnDisable = false,
        isDarkTheme = false,
        category = 'equipment',
        baseRoute,
        setIndexOfSelected = undefined,
        linkUrl = false,
    },
) => {
    const [isSelectOpen, setIsSelectOpen] = useState(false);
    const [isLoadingRouteChange, setIsLoadingRouteChange] = useState(false);
    const [idxOfSelected, setIdxOfSelected] = useState(0);

    const isAbTestCreditEnabled = useMemo(() => isCreditAbnEnabled(), []);

    const changeOptionsHandler = useCallback((option, idx) => {
        baseDataLayerPush({
            event: EVENTS.UAevent,
            category,
            action: option.value > 0 ? `leasing_duration_${option.value}` : 'buy',
            label: name,
        });
        setIndexOfSelected && setIndexOfSelected(idx);
        setIdxOfSelected(idx);
        setSelectedOption(option);
        setIsSelectOpen(false);
    }, []);

    const orderClickHandlerWrap = ({ selectedOption, name, idxOfSelected }) => {
        setIsLoadingRouteChange(true);
        orderClickHandler({ selectedOption, name, idxOfSelected });

        // @TODO Refactoring. Need to replace baseRoute prop to the EShopCategory
        const shopCategory = getShopCategoryByRoute(baseRoute, EQUIPMENT_ROUTES_MAP);

        if (shopCategory) {
            fullBuyEquipAnalyticsBase.clickedToOrder({
                equipmentTitle: name,
                category: shopCategory,
                leasing: selectedOption.value,
                cost: selectedOption.pricem,
            });
            return;
        }

        if (baseRoute === '/lk/change-tariff') {
            baseDataLayerPush({
                event: EVENTS.UAevent,
                category: CATEGORIES.tariffChange,
                action: ACTIONS.chooseRouter,
                label: `router ${name} ${selectedOption.price}|${selectedOption.value}`,
            });
        }

        !isRouteChanging && setIsLoadingRouteChange(false);
    };

    const toggleSelectHandler = useCallback(() => {
        // TODO - передается объект в функцию, которая с ним не работает...
        if (!isSelectOpen) {
            dataLayerPush({
                event: EVENTS.UAevent,
                category,
                action: 'choose_price',
                label: name,
            });
        }

        if (!isSelectDisable) {
            setIsSelectOpen(!isSelectOpen);
        }
    }, [name, isSelectDisable, isSelectOpen]);

    const isDisabledButton = useCallback(() => {
        const { orderStatus, isLoadingOrder } = loading;
        return orderStatus || isLoadingOrder;
    }, [loading]);

    const getCurrency = useCallback((option) => {
        if (option) {
            return option.type === EPriceTypes.Possess
            ? ' ₽'
            : ' ₽/мес';
        }
        if (!selectedOption) {
            return;
        }
        return selectedOption.type === EPriceTypes.Possess
            ? ' ₽'
            : ' ₽/мес';
    }, [selectedOption]);

    const getButtonType = useCallback(() => {
        if (isDarkTheme) {
            return btnTypes.WHITE;
        }

        const { orderStatus } = loading;
        return orderStatus ? btnTypes.SECONDARY : btnTypes.PRIMARY;
    }, [isDarkTheme, loading]);

    const getButtonText = useCallback(() => {
        const { orderStatus } = loading;
        return !orderStatus ? orderText || 'Заказать' : 'Заказано';
    }, [loading, orderText]);

    const getPriceLabel = useCallback((option) => {
        if (isAbTestCreditEnabled) {
            if (priceOptions.length > 1) {
                return 'Доступна рассрочка';
            }
            return 'Полная стоимость';
        }
        if (option) {
            return option.type === EPriceTypes.Possess
                ? 'Полная стоимость'
                : `Рассрочка ${option.value} мес.`;
        }
        if (!selectedOption) {
            return;
        }
        return selectedOption.type === EPriceTypes.Possess
            ? 'Полная стоимость'
            : `Рассрочка ${selectedOption.value} мес.`;
    }, [selectedOption, priceOptions.length]);

    const [isRouteChanging] = useRouterChange({
        onComplete: () => setIsLoadingRouteChange(false),
    });

    return (
        <ST.PriceBlock>
            <ST.Price
                onClick={isAbTestCreditEnabled ? undefined : toggleSelectHandler}
                isMoreOneOptions={!isAbTestCreditEnabled && priceOptions.length > 1}
                data-test='router-item-price'
            >
                {withSelect && selectedOption &&
                    <>
                        {
                            isNaN(selectedOption?.price)
                                ? ''
                                : <ST.Leasing isMoreOneOptions={!isAbTestCreditEnabled && priceOptions.length > 1} isDarkTheme={isDarkTheme}>
                                    {getPriceLabel()}
                                </ST.Leasing>
                        }
                        {
                            (isSelectOpen && priceOptions.length > 1) &&
                                <ST.Popup
                                    data-test='router-item-popup'
                                    isOpen={isSelectOpen}
                                    onClose={toggleSelectHandler}
                                    onClick={(e) => e.stopPropagation()}
                                    fullWidth={true}
                                    width='360px'
                                    height='auto'>
                                    <ST.PopupTitle>Варианты покупки</ST.PopupTitle>
                                    <ST.EquipName>{name}</ST.EquipName>
                                    <>
                                        {priceOptions && priceOptions.map((option, i) => {
                                            return (<ST.OptionButton
                                                    key={i}
                                                    type={btnTypes[option.requestId ? 'PRIMARY' : 'SECONDARY']}
                                                    onClick={() => changeOptionsHandler(option, i)}
                                                >
                                                    <span>{getPriceLabel(option)}</span>
                                                    <span>{toCurrency(option.pricePromo || option.price)}{getCurrency(option)}</span>
                                            </ST.OptionButton>);
                                        })}
                                    </>
                                </ST.Popup>
                        }
                    </>
                }
                <ST.TextButton isDarkTheme={isDarkTheme}>
                    {
                        selectedOption?.pricePromo
                            ? toCurrency(selectedOption.pricePromo)
                            : isNaN(selectedOption?.price) ? '' : toCurrency(selectedOption?.price)
                    }
                    {isNaN(selectedOption?.price) ? '' : getCurrency()}
                </ST.TextButton>
            </ST.Price>
            {linkUrl
                ? (
                    <Link href={linkUrl}>
                        <BaseButton type={getButtonType()} size={btnSizes.SMALL}>{getButtonText()}</BaseButton>
                    </Link>
                )
                : (
                    <BaseButton
                        onClick={() => orderClickHandlerWrap({ selectedOption, name, idxOfSelected })}
                        data-test={dataTest}
                        text={getButtonText()}
                        type={getButtonType()}
                        disabled={(isRouteChanging && !isLoadingRouteChange) || isBtnDisable || isDisabledButton()}
                        size={btnSizes.SMALL}
                        isLoading={isLoadingRouteChange}
                    />
                )}
        </ST.PriceBlock>
    );
};

export default PurchaseBlock;
